// draggableNode.js

import React from 'react';
import { cn } from './lib/utils';
import {
  Download, Upload, Brain, Type, Filter, Split,
  Merge, Clock, GitBranch, RefreshCw, BarChart,
  Globe, Database, Shield
} from 'lucide-react';

/**
 * DraggableNode component for pipeline editor toolbar
 * Enhanced to support all node types with consistent theming
 *
 * @param {Object} props - Component props
 * @param {string} props.type - Node type (any supported node type)
 * @param {string} props.label - Display label for the node
 * @param {string} props.className - Optional additional CSS classes
 * @returns {React.Component} Draggable node component
 */
export const DraggableNode = ({ type, label, className = '' }) => {
    const onDragStart = (event, nodeType) => {
      const appData = { nodeType };
      event.target.style.cursor = 'grabbing';
      event.dataTransfer.setData('application/reactflow', JSON.stringify(appData));
      event.dataTransfer.effectAllowed = 'move';
    };

    // Map node types to their corresponding icons
    const getNodeIcon = (nodeType) => {
      switch (nodeType) {
        case 'customInput':
          return <Download className="w-3 h-3" />;
        case 'customOutput':
          return <Upload className="w-3 h-3" />;
        case 'llm':
          return <Brain className="w-3 h-3" />;
        case 'text':
          return <Type className="w-3 h-3" />;
        case 'filter':
          return <Filter className="w-3 h-3" />;
        case 'join':
          return <Merge className="w-3 h-3" />;
        case 'split':
          return <Split className="w-3 h-3" />;
        case 'delay':
          return <Clock className="w-3 h-3" />;
        case 'conditional':
          return <GitBranch className="w-3 h-3" />;
        case 'transform':
          return <RefreshCw className="w-3 h-3" />;
        case 'aggregate':
          return <BarChart className="w-3 h-3" />;
        case 'api':
          return <Globe className="w-3 h-3" />;
        case 'storage':
          return <Database className="w-3 h-3" />;
        case 'validator':
          return <Shield className="w-3 h-3" />;
        default:
          return <Type className="w-3 h-3" />;
      }
    };

    return (
      <div
        className={cn(
          'cursor-grab px-2 py-1 flex items-center gap-1.5',
          'rounded-lg transition-all duration-200 hover:scale-105',
          'select-none user-select-none shadow-sm hover:shadow-md',
          'text-sm font-medium',
          // Use passed className if provided, otherwise default styling
          className || 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 hover:border-slate-300'
        )}
        onDragStart={(event) => onDragStart(event, type)}
        onDragEnd={(event) => (event.target.style.cursor = 'grab')}
        draggable
      >
        {getNodeIcon(type)}
        <span>
          {label}
        </span>
      </div>
    );
  };
  